/**
 * Secuencia corta al terminar la llamada (remoto colgó o red cortó).
 * Aproxima el patrón de “ocupado / reorden” US (480+620 Hz) que muchos móviles reproducen
 * al cortar, en lugar de pitidos muy cortos tipo central antigua.
 * Usa el mismo AudioContext que el teclado DTMF.
 */

import { getSharedAudioContext } from '@/lib/dtmfKeypad';

/** Dual tone estilo señal de ocupado/reorden (ANSI T1.401 / similar a muchos terminales). */
const F1 = 480;
const F2 = 620;

export interface PlayCallEndToneOptions {
  /** Número de pulsos audibles. */
  bursts?: number;
  /** Duración de cada pulso en ms (tono encendido). */
  burstMs?: number;
  /** Silencio entre pulsos en ms. */
  gapMs?: number;
  gain?: number;
}

/**
 * Reproduce la secuencia de tonos al colgar / fin de llamada.
 * No hace nada si Web Audio no está disponible.
 */
export function playCallEndTone(options?: PlayCallEndToneOptions): void {
  const bursts = options?.bursts ?? 3;
  const burstMs = options?.burstMs ?? 220;
  const gapMs = options?.gapMs ?? 200;
  const peakGain = options?.gain ?? 0.085;

  const ctx = getSharedAudioContext();
  if (!ctx || ctx.state === 'closed') return;
  void ctx.resume(); // el guard !== 'closed' era codigo muerto: la linea anterior ya retorno en ese caso

  const burstSec = burstMs / 1000;
  const gapSec = gapMs / 1000;
  const step = burstSec + gapSec;

  for (let i = 0; i < bursts; i++) {
    const t0 = ctx.currentTime + 0.015 + i * step;
    const master = ctx.createGain();
    const attack = 0.004;
    const release = Math.min(0.04, burstSec * 0.25);
    master.gain.setValueAtTime(0.0001, t0);
    master.gain.exponentialRampToValueAtTime(peakGain, t0 + attack);
    master.gain.setValueAtTime(peakGain, t0 + burstSec - release);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + burstSec);

    const o1 = ctx.createOscillator();
    o1.type = 'sine';
    o1.frequency.setValueAtTime(F1, t0);
    o1.connect(master);

    const o2 = ctx.createOscillator();
    o2.type = 'sine';
    o2.frequency.setValueAtTime(F2, t0);
    o2.connect(master);

    master.connect(ctx.destination);
    o1.start(t0);
    o2.start(t0);
    o1.stop(t0 + burstSec + 0.02);
    o2.stop(t0 + burstSec + 0.02);
  }
}
